/**
 * useDebounce Hook
 *
 * @module shared/hooks/useDebounce
 *
 * Debounces a value with a configurable delay.
 * Useful for search inputs, filters, and other scenarios where you want to
 * delay updates until the user stops typing.
 *
 * @example
 * ```tsx
 * const [searchText, setSearchText] = useState('');
 * const debouncedSearch = useDebounce(searchText, 500);
 *
 * useEffect(() => {
 *   // This will only run after 500ms of no changes
 *   handleSearch(debouncedSearch);
 * }, [debouncedSearch]);
 * ```
 */

import { useEffect, useState } from 'react';

/**
 * Debounces a value with the specified delay
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds (default: 500)
 * @returns The debounced value
 */
export function useDebounce<T>(value: T, delay: number = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
